import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@workspace/ui/lib/utils";
import { Button } from "../atoms/button.js";
import { Icon, type IconName } from "../atoms/icon.js";

const buttonDropdownVariants = cva(
    "relative inline-block text-left",
    {
        variants: {
            size: {
                sm: "",
                md: "",
                lg: "",
            },
        },
        defaultVariants: {
            size: "md",
        },
    }
);

export interface ButtonDropdownOption {
    label?: string;
    value: string;
    icon?: IconName;
    disabled?: boolean;
    onClick?: () => void;
}

export interface ButtonDropdownProps
    extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>,
    VariantProps<typeof buttonDropdownVariants> {
    title?: string;
    label?: string;
    options: ButtonDropdownOption[];
    value?: string;
    onOptionChange?: (value: string) => void;
    onChange?: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    icon?: IconName;
    horizontalPosition?: "start" | "end";
}

export const ButtonDropdown = React.forwardRef<HTMLDivElement, ButtonDropdownProps>(
    ({
        className,
        size,
        title,
        label,
        options,
        value,
        onOptionChange,
        onChange,
        placeholder = "Select option...",
        disabled = false,
        icon,
        horizontalPosition = "end",
        ...props
    }, ref) => {
        const [isOpen, setIsOpen] = React.useState(false);

        const selectedOption = options.find(opt => opt.value === value);

        const handleSelect = (option: ButtonDropdownOption) => {
            if (option.disabled) return;

            onOptionChange?.(option.value);
            onChange?.(option.value);
            option.onClick?.();
            setIsOpen(false);
        };

        const triggerLabel = label ?? title ?? "Select";

        return (
            <div
                ref={ref}
                className={cn(buttonDropdownVariants({ size }), className)}
                {...props}
            >
                <Button
                    variant="outline"
                    className="w-full justify-between"
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    disabled={disabled}
                    aria-label={triggerLabel}
                >
                    <div className="flex items-center gap-2">
                        {icon && <Icon name={icon} size="sm" />}
                        <span>{selectedOption?.label || placeholder}</span>
                    </div>
                    <Icon name="chevron-down" size="sm" />
                </Button>

                {isOpen && (
                    <>
                        {/* Backdrop */}
                        <div
                            className="fixed inset-0 z-10"
                            onClick={() => setIsOpen(false)}
                        />

                        {/* Dropdown Menu */}
                        <div className={cn(
                            "absolute z-20 mt-2 w-full min-w-[200px] rounded-md border bg-popover p-1 shadow-lg",
                            horizontalPosition === "start" ? "left-0" : "right-0"
                        )}>
                            {options.map((option) => (
                                <button
                                    key={option.value}
                                    className={cn(
                                        "flex w-full items-center gap-2 rounded-sm px-3 py-2 text-sm transition-colors",
                                        "hover:bg-accent hover:text-accent-foreground",
                                        "focus:bg-accent focus:text-accent-foreground focus:outline-none",
                                        option.disabled && "opacity-50 cursor-not-allowed hover:bg-transparent",
                                        value === option.value && "bg-accent text-accent-foreground"
                                    )}
                                    onClick={() => handleSelect(option)}
                                    disabled={option.disabled}
                                >
                                    {option.icon && <Icon name={option.icon} size="sm" />}
                                    <span>{option.label}</span>
                                </button>
                            ))}
                        </div>
                    </>
                )}
            </div>
        );
    }
);

ButtonDropdown.displayName = "ButtonDropdown";

// Default export for backward compatibility
export default ButtonDropdown;
